// apps/frontend/src/components/channels/TranscriptPanel.tsx

import { useEffect, useRef } from 'react';

import { ToolResultDisplay } from './ToolResultDisplay';

import type { DisplayConfig } from './ToolResultDisplay';
import type { TimelineEntry } from '@/hooks/useChannelSession';

interface TranscriptPanelProps {
  entries: TimelineEntry[];
  toolDisplays?: Record<string, DisplayConfig>;
  emptyMessage?: string;
}

export const TranscriptPanel = ({
  entries,
  toolDisplays,
  emptyMessage = 'Start the session and speak into your microphone to see the conversation.',
}: TranscriptPanelProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [entries.length]);

  if (entries.length === 0) {
    return <p className="text-muted-foreground text-sm">{emptyMessage}</p>;
  }

  return (
    <div ref={scrollRef} className="h-full space-y-2 overflow-y-auto pr-1">
      {entries.map((entry) => {
        if (entry.type === 'tool_executed') {
          return (
            <ToolResultDisplay
              key={entry.id}
              toolName={entry.toolName}
              toolResult={entry.toolResult}
              timestamp={entry.timestamp}
              displayConfig={toolDisplays?.[entry.toolName]}
            />
          );
        }

        return (
          <div key={entry.id} className="rounded border bg-white p-2 text-sm dark:bg-slate-950">
            <div className="mb-1 flex items-center gap-2 text-xs font-semibold">
              <span>{entry.role === 'user' ? 'You' : 'AI Agent'}</span>
              {entry.replay ? <span className="text-muted-foreground">Replay</span> : null}
              <span className="text-muted-foreground ml-auto text-[10px]">
                {new Date(entry.timestamp).toLocaleTimeString()}
              </span>
            </div>
            <p className="whitespace-pre-wrap">{entry.content}</p>
          </div>
        );
      })}
    </div>
  );
};
